import type {
  AdminAppsResponse,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminsControllerAuthorizeAppTypeEnum,
  AdminInviteSetupRequest,
  AuthorizedAdminResponse,
  Verify2FAOtpResponse,
} from '@coretava/api/security-service';
import { adminsApi } from '@/shared/lib/api/security-client';
import { apiClient, getGamixAppId } from '@/shared/lib/api/client';

export type MeResponse = AdminAppsResponse & {
  scopes?: string[];
  featureFlags?: string[];
  roleKey?: string;
};

const bearer = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export async function login(data: AdminLoginRequest): Promise<AdminLoginResponse> {
  const response = await adminsApi.adminsControllerLogin({ adminLoginRequest: data });
  return response.data;
}

export async function verify2fa(pending2FAToken: string, code: string): Promise<Verify2FAOtpResponse> {
  const response = await adminsApi.adminsControllerVerifyAdminLoginOtp(
    { verify2FAOtpRequest: { code } },
    bearer(pending2FAToken),
  );
  return response.data;
}

export async function resend2fa(pending2FAToken: string): Promise<void> {
  await adminsApi.adminsControllerResend2FACode(bearer(pending2FAToken));
}

export async function logout(): Promise<void> {
  await adminsApi.adminsControllerLogout();
}

export async function fetchMe(appType?: AdminsControllerAuthorizeAppTypeEnum): Promise<MeResponse> {
  /* `gamixAppId` is injected by the apiClient interceptor when an active app exists. The
   * regenerated OpenAPI spec types it as required, so we forward whatever the interceptor
   * has — empty string when bootstrapping with no active app, which is fine because the BE
   * tolerates a missing header on this endpoint. */
  const response = await adminsApi.adminsControllerAuthorize({ gamixAppId: getGamixAppId() ?? '', appType });
  return response.data as MeResponse;
}

export async function forgotPassword(email: string): Promise<void> {
  await adminsApi.adminsControllerForgetPassword({
    // `isCorepilot` is not in the generated type yet — cast until the OpenAPI client is regenerated
    adminPasswordResetRequest: { email, isCorepilot: true } as { email: string } & { isCorepilot: boolean },
  });
}

export async function verifyResetOtp(email: string, code: string): Promise<AuthorizedAdminResponse> {
  const response = await adminsApi.adminsControllerVerifyAdminOtp({
    adminOtpVerifyRequest: { email, code },
  });
  return response.data;
}

export async function resetPassword(data: AdminInviteSetupRequest, token: string): Promise<void> {
  await adminsApi.adminsControllerUpdatePassword(
    { adminInviteSetupRequest: data },
    bearer(token),
  );
}

/**
 * Best-effort post-/setup hooks that flip the per-app `authorizedapps.status`
 * to `active` and bootstrap the caller's `agentProfiles` row so the routing
 * tool can actually pick them.
 *
 * `gamix-app-id` is intentionally NOT passed per-call: the request
 * interceptor in `shared/lib/api/client.ts` unconditionally overwrites the
 * header with the module-level `gamixAppId`, so a per-call override would
 * be silently dropped. Callers MUST pin the active workspace via
 * `useAuthStore.setActiveApp(...)` (which propagates to `gamixAppId` via
 * the `api-sync` subscription) BEFORE invoking this — `SetupPage` does so
 * right after `applyAuthorized` to guard against a stale `corepilot.activeApp`
 * value in localStorage from a prior session.
 *
 * Failures are NOT propagated — the user has already passed the OTP +
 * password gate at this point and we don't want a transient 4xx/5xx from a
 * bootstrap call to strand them on a half-onboarded screen. The two BE
 * endpoints are idempotent, so a stale-deploy retry is a no-op next time
 * the user logs in.
 *
 * Why not call from the BE during `verifyOtp`/`updatePassword`? Both BE
 * endpoints are shared with the /forgot-password flow, where flipping
 * authorizedapps + creating an agentProfile would be wrong. Doing it from
 * the FE keeps the side-effect scoped to the invite-acceptance code path.
 */
export async function finalizeInvitation(adminId: string): Promise<void> {
  await Promise.allSettled([
    apiClient.patch(`/v2/admins/${adminId}/invite/accept`),
    // coreleads-service is mounted at `/coreleads/*` directly (no `/v2`
    // prefix the way security-service has) — see the existing list/messages
    // calls in apps/web for the same convention.
    apiClient.post('/coreleads/agent-profiles/me'),
  ]);
}
